/**
 * The Sponsor's queue — **proposals and claims together**, oldest first (D-30).
 *
 * One list, because a Sponsor's job is not "review proposals" and separately "review claims": it
 * is "what needs my signature". Splitting them into two screens is how the second one gets
 * forgotten.
 *
 * A Sponsor's own proposal skips this queue rather than appearing in it — endorsing your own
 * submission is one signature pretending to be two (D-39) — so an empty queue here really does
 * mean nothing is waiting.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import {
  ClaimStatusBadge,
  Money,
  PageHeader,
  ProposalStatusBadge,
  RefLink,
} from '@/components/bits';
import { Card, EmptyState, Table, Td, Th } from '@/components/ui';
import { activityLabel } from '@/lib/policy';
import { daysBetween, formatDate, sgtToday } from '@/lib/dates';

export function Approvals() {
  const session = useSession();

  const proposals = useQuery({
    queryKey: ['proposals', 'sponsor', session.year],
    queryFn: () =>
      api.proposals({ budget_year: session.year, status: ['sponsor_review'], limit: 100 }),
  });
  const claims = useQuery({
    queryKey: ['claims', 'sponsor', session.year],
    queryFn: () =>
      api.claims({ budget_year: session.year, status: ['sponsor_review'], limit: 100 }),
  });

  const today = sgtToday();
  const waiting = (stamp: string): number => daysBetween(stamp.slice(0, 10), today);

  const rows = [
    ...(proposals.data?.items ?? []).map((item) => ({
      key: `p${item.id}`,
      kind: 'proposal' as const,
      to: `/proposals/${item.id}`,
      ref: item.ref_no,
      what: item.activity_title,
      type: item.activity_type,
      department: item.department_name,
      date: item.proposed_date,
      amount: item.requested_amount_cents ?? item.computed_pool_cents,
      since: item.submitted_at ?? item.updated_at,
      status: item.status,
    })),
    ...(claims.data?.items ?? []).map((item) => ({
      key: `c${item.id}`,
      kind: 'claim' as const,
      to: `/claims/${item.id}`,
      ref: item.ref_no,
      what: activityLabel(item.activity_type),
      type: item.activity_type,
      department: item.department_name,
      date: item.actual_date,
      amount: item.total_cost_cents,
      since: item.submitted_at ?? item.updated_at,
      status: item.status,
    })),
  ].sort((a, b) => a.since.localeCompare(b.since));

  return (
    <>
      <PageHeader
        title="Waiting on you"
        subtitle="Proposals before the event and claims after it, oldest first."
      />

      <Card title={`${rows.length} to endorse`}>
        {proposals.isPending || claims.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing needs your signature">
            Proposals you raised yourself skip this queue — endorsing your own submission would be
            one signature pretending to be two (D-39).
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>What</Th>
                <Th>Department</Th>
                <Th>Event date</Th>
                <Th numeric>Amount</Th>
                <Th>Waiting</Th>
                <Th>Stage</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const days = waiting(row.since);
                return (
                  <tr key={row.key}>
                    <Td>
                      <RefLink to={row.to} refNo={row.ref} />
                      <p className="text-muted-foreground text-xs">
                        {row.kind === 'proposal' ? 'before the event' : 'after the event'}
                      </p>
                    </Td>
                    <Td>
                      {row.what}
                      <p className="text-muted-foreground text-xs">{activityLabel(row.type)}</p>
                    </Td>
                    <Td className="text-xs">{row.department}</Td>
                    <Td className="whitespace-nowrap">{formatDate(row.date)}</Td>
                    <Td numeric>
                      <Money cents={row.amount} />
                    </Td>
                    <Td>
                      <span
                        className={days >= 7 ? 'text-[var(--color-warning)] text-sm' : 'text-sm'}
                      >
                        {days === 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'}`}
                      </span>
                    </Td>
                    <Td>
                      {row.kind === 'proposal' ? (
                        <ProposalStatusBadge status={row.status} />
                      ) : (
                        <ClaimStatusBadge status={row.status} />
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
